import { managesDevicesAcrossCompanies } from 'src/app/models/user.model';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs';
import { CompanyNameType } from 'src/app/models/company.model';
import { DeviceDto, DeviceUpdate } from 'src/app/models/device.model';
import { DeviceService } from 'src/app/services/device.service';
import { MasterAdminCompanyService } from 'src/app/services/master-admin-company.service';
import { selectProfileData } from 'src/app/stores/profile/profile.selector';
import { LoadingState } from 'src/app/stores/store.states';

export interface EditDeviceModalData {
  device?: DeviceDto;
}

@Component({
  selector: 'app-edit-device-modal',
  templateUrl: './edit-device-modal.component.html',
  styleUrl: './edit-device-modal.component.scss',
  standalone: false,
})
export class EditDeviceModalComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  device?: DeviceDto;
  errorMessage = '';
  loadingCompanies = false;
  canAssignCompany = false;
  companies: CompanyNameType[] = [];
  selectedCompany: CompanyNameType | null = null;
  form: FormGroup;
  protected readonly FormControl = FormControl;

  constructor(
    public dialogRef: DialogRef<boolean>,
    @Inject(DIALOG_DATA) public data: EditDeviceModalData,
    private readonly formBuilder: FormBuilder,
    private readonly deviceService: DeviceService,
    private readonly masterAdminCompanyService: MasterAdminCompanyService,
    private readonly store: Store,
  ) {
    this.device = data?.device;
    this.form = this.formBuilder.group({
      name: [this.device?.nameAlias ?? '', [Validators.maxLength(160)]],
      description: [
        this.device?.description ?? '',
        [Validators.maxLength(1024)],
      ],
      companyId: [this.device?.company?.id ?? '', [Validators.maxLength(120)]],
    });
    this.selectedCompany = this.getCurrentDeviceCompany();
  }

  ngOnInit(): void {
    this.store
      .select(selectProfileData)
      .pipe(
        filter(
          (profile) =>
            profile.quantumProfileLoadingState === LoadingState.SUCCESS ||
            profile.quantumProfileLoadingState === LoadingState.ERROR,
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((profile) => {
        this.canAssignCompany = managesDevicesAcrossCompanies(profile);

        if (this.canAssignCompany) {
          this.enableCompanySelection();
        }
      });
  }

  abortModal(): void {
    this.dialogRef.close(false);
  }

  editDevice(): void {
    const deviceId = this.device?.deviceId?.trim();
    if (!deviceId) {
      this.errorMessage = 'Missing device ID';
      return;
    }

    const payload: DeviceUpdate = {
      nameAlias: this.form.value.name,
      description: this.form.value.description,
    };

    if (this.canAssignCompany) {
      const companyId = this.form.value.companyId?.trim();
      if (companyId) {
        payload.companyId = companyId;
      }
    }

    this.deviceService.updateDevice(deviceId, payload).subscribe({
      next: () => {
        this.dialogRef.close(true);
      },
      error: () => {
        this.errorMessage = 'Update failed';
        console.error('Update failed');
      },
    });
  }

  get companyPreSelectedValue(): CompanyNameType | string | null {
    if (this.selectedCompany) {
      return this.selectedCompany;
    }

    const companyId = this.form.value.companyId?.trim();
    return companyId || null;
  }

  onCompanySelected(selected: CompanyNameType | string): void {
    const company =
      typeof selected === 'string'
        ? this.findCompanyByNameOrId(selected)
        : selected;

    this.selectedCompany = company ?? null;
    this.form.controls['companyId'].setValue(this.selectedCompany?.id ?? '');
    this.form.controls['companyId'].markAsTouched();
  }

  companyFormatter = (item: CompanyNameType | null, value: string): string => {
    if (!item) {
      return value;
    }

    return item.name;
  };

  private enableCompanySelection(): void {
    this.loadCompanies();
  }

  private loadCompanies(): void {
    this.loadingCompanies = true;
    this.masterAdminCompanyService
      .getAllCompanies()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (companies) => {
          this.companies = this.includeCurrentCompany(companies);
          this.selectedCompany =
            this.findCompanyById(this.form.value.companyId) ??
            this.selectedCompany;
          this.loadingCompanies = false;
        },
        error: () => {
          this.companies = this.includeCurrentCompany([]);
          this.loadingCompanies = false;
        },
      });
  }

  private includeCurrentCompany(
    companies: CompanyNameType[],
  ): CompanyNameType[] {
    const currentCompany = this.getCurrentDeviceCompany();
    if (!currentCompany) {
      return companies;
    }

    const hasCurrentCompany = companies.some(
      (company) =>
        company.id.trim().toLowerCase() ===
        currentCompany.id.trim().toLowerCase(),
    );

    return hasCurrentCompany ? companies : [currentCompany, ...companies];
  }

  private getCurrentDeviceCompany(): CompanyNameType | null {
    const companyId = this.device?.company?.id?.trim();
    if (!companyId) {
      return null;
    }

    return {
      id: companyId,
      name: this.device?.company?.name?.trim() || companyId,
    };
  }

  private findCompanyById(companyId?: string): CompanyNameType | undefined {
    const normalized = companyId?.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }

    return this.companies.find(
      (company) => company.id.trim().toLowerCase() === normalized,
    );
  }

  private findCompanyByNameOrId(value?: string): CompanyNameType | undefined {
    const raw = value?.trim();
    const normalized = raw?.toLowerCase();
    if (!raw || !normalized) {
      return undefined;
    }

    const parsedId =
      this.extractCompanyIdFromFormattedValue(raw)?.toLowerCase();

    return this.companies.find(
      (company) =>
        company.id.toLowerCase() === normalized ||
        company.name.toLowerCase() === normalized ||
        `${company.name} (${company.id})`.toLowerCase() === normalized ||
        (!!parsedId && company.id.toLowerCase() === parsedId),
    );
  }

  private extractCompanyIdFromFormattedValue(
    value: string,
  ): string | undefined {
    const match = value.match(/\(([^()]+)\)\s*$/);
    return match?.[1]?.trim();
  }
}
